import React from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import TitleIcon from '@mui/icons-material/Title';

/**
 * Maps mui-rte control names to Tiptap actions. Each entry has:
 *   - run(editor): perform the action
 *   - isActive(editor): true if this mark/node is active at the cursor
 *   - icon: React node
 *   - title: tooltip text
 *   - dividerAfter: render a divider after this button when true
 */
export const BUTTONS = {
  undo: {
    icon: <UndoIcon fontSize="inherit" />,
    title: 'Undo',
    run: (editor) => editor.chain().focus().undo().run(),
    canRun: (editor) => editor.can().undo(),
  },
  redo: {
    icon: <RedoIcon fontSize="inherit" />,
    title: 'Redo',
    run: (editor) => editor.chain().focus().redo().run(),
    canRun: (editor) => editor.can().redo(),
    dividerAfter: true,
  },
  title: {
    icon: <TitleIcon fontSize="inherit" />,
    title: 'Heading',
    run: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
  },
  bold: {
    icon: <FormatBoldIcon fontSize="inherit" />,
    title: 'Bold',
    run: (editor) => editor.chain().focus().toggleBold().run(),
    isActive: (editor) => editor.isActive('bold'),
  },
  italic: {
    icon: <FormatItalicIcon fontSize="inherit" />,
    title: 'Italic',
    run: (editor) => editor.chain().focus().toggleItalic().run(),
    isActive: (editor) => editor.isActive('italic'),
  },
  underline: {
    icon: <FormatUnderlinedIcon fontSize="inherit" />,
    title: 'Underline',
    run: (editor) => editor.chain().focus().toggleUnderline().run(),
    isActive: (editor) => editor.isActive('underline'),
  },
  strikethrough: {
    icon: <FormatStrikethroughIcon fontSize="inherit" />,
    title: 'Strikethrough',
    run: (editor) => editor.chain().focus().toggleStrike().run(),
    isActive: (editor) => editor.isActive('strike'),
  },
  highlight: {
    icon: <BorderColorIcon fontSize="inherit" />,
    title: 'Highlight',
    run: (editor) => editor.chain().focus().toggleHighlight().run(),
    isActive: (editor) => editor.isActive('highlight'),
    dividerAfter: true,
  },
  link: {
    icon: <LinkIcon sx={{
      fontSize: "inherit"
    }} />,
    title: 'Link',
    run: (editor) => {
      const previous = editor.getAttributes('link').href;
      // eslint-disable-next-line no-alert
      const url = window.prompt('Enter URL', previous || 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    },
    isActive: (editor) => editor.isActive('link'),
  },
  media: {
    icon: <ImageIcon fontSize="inherit" />,
    title: 'Image',
    run: (editor) => {
      // eslint-disable-next-line no-alert
      const url = window.prompt('Image URL');
      if (url) editor.chain().focus().setImage({ src: url }).run();
    },
  },
  numberList: {
    icon: <FormatListNumberedIcon fontSize="inherit" />,
    title: 'Numbered list',
    run: (editor) => editor.chain().focus().toggleOrderedList().run(),
    isActive: (editor) => editor.isActive('orderedList'),
  },
  bulletList: {
    icon: <FormatListBulletedIcon fontSize="inherit" />,
    title: 'Bullet list',
    run: (editor) => editor.chain().focus().toggleBulletList().run(),
    isActive: (editor) => editor.isActive('bulletList'),
  },
  quote: {
    icon: <FormatQuoteIcon fontSize="inherit" />,
    title: 'Blockquote',
    run: (editor) => editor.chain().focus().toggleBlockquote().run(),
    isActive: (editor) => editor.isActive('blockquote'),
  },
  code: {
    icon: <CodeIcon fontSize="inherit" />,
    title: 'Code',
    run: (editor) => editor.chain().focus().toggleCode().run(),
    isActive: (editor) => editor.isActive('code'),
    dividerAfter: true,
  },
  clear: {
    icon: <FormatClearIcon fontSize="inherit" />,
    title: 'Clear formatting',
    run: (editor) => editor.chain().focus().unsetAllMarks().clearNodes().run(),
  },
};
